import { useMemo, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

import { useGameSelection } from '@/hooks/useGameSelection';
import { useAppSelector } from '@/store/hooks';
import { useProfilesQuery } from '@/services/endpoints/games';
import { GamePicker } from './GamePicker';
import { cn } from '@/lib/cn';
import type { Profile } from '@/types';

interface GameHeaderProps {
  className?: string;
}

/**
 * Game selector — shows the active game alongside the user's QLASH nickname
 * and the game-specific gamertag (Clash Royale player tag, Riot
 * `gameName#tagLine`, etc.). Tap opens `GamePicker` to switch.
 *
 * Mirrors `mobile-app/src/components/hub/hub-header.tsx` lines 288–343 where
 * the "Your {gameName} id :" card sits below the banner — we collapse both
 * pieces into a single card so the Mini App's vertical budget is respected.
 */
export function GameHeader({ className }: GameHeaderProps) {
  const { activeGame } = useGameSelection();
  const me = useAppSelector((s) => s.auth.user);
  const userId = typeof me?.id === 'number' ? me.id : Number(me?.id);
  const hasUser = !!me && Number.isFinite(userId) && userId > 0;
  const { data: profiles } = useProfilesQuery(userId, { skip: !hasUser });
  const [open, setOpen] = useState(false);

  const activeProfile = useMemo<Profile | null>(() => {
    if (!activeGame) return null;
    const list = profiles?.data ?? [];
    // Backend serialises via `ShortProfile`, which omits the flat `game_id`
    // field and instead nests the game in `profile.game`. Older mobile-app
    // builds resolve via either path, so check both for safety.
    return (
      list.find((p) => {
        const flat = (p as unknown as { game_id?: number }).game_id;
        const nested = (p as unknown as { game?: { id?: number } }).game?.id;
        return flat === activeGame.id || nested === activeGame.id;
      }) ?? null
    );
  }, [profiles, activeGame]);

  const gamertag = formatGamertag(activeProfile);
  const accountName = me?.username ?? me?.firstName ?? null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex w-full items-center justify-end gap-3 rounded-2xl p-3 text-right transition-opacity active:opacity-80',
          activeGame
            ? 'bg-bg-secondary'
            : 'border border-dashed border-border bg-transparent',
          className,
        )}
      >
        {activeGame ? (
          <>
            <div className="min-w-0 max-w-[60%]">
              <p className="truncate text-sm font-bold text-fg">{activeGame.name}</p>
              {accountName && (
                <p className="truncate text-xs text-fg-muted">{accountName}</p>
              )}
              <p className="truncate text-xs font-semibold text-brand">
                {gamertag ?? '— no profile yet —'}
              </p>
            </div>
            <span className="flex h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-bg-tertiary ring-1 ring-brand">
              {activeGame.icon && (
                <img
                  src={activeGame.icon}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              )}
            </span>
            <ChevronDown aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-fg">Select your game</span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-fg-muted">
              <Plus aria-hidden className="h-5 w-5" />
            </span>
          </>
        )}
      </button>

      <GamePicker open={open} onClose={() => setOpen(false)} />
    </>
  );
}

interface GameInfoLike {
  id?: string;
  gameName?: string;
  tagLine?: string;
}

function formatGamertag(profile: Profile | null): string | null {
  if (!profile) return null;
  const info = profile.info as GameInfoLike | undefined;
  if (info?.gameName && info?.tagLine) return `${info.gameName}#${info.tagLine}`;
  if (info?.id && info.id !== 'unknown') return info.id;
  return null;
}
